import { type ElementType, type ReactNode } from "react";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { mergeSx } from "ui/merge-sx";
import Flash from "components/partials/flash/flash";

import { classMerge } from "utilities/css";

interface LayoutSectionProps {
  component?: ElementType;
  className?: string;
  children?: ReactNode;
  sx?: SxProps<Theme>;
  "data-testid"?: string;
}

const CONTENT_WRAPPER_SX = {
  display: "flex",
  flex: "1",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
  mt: "0!important",
  position: "relative",
} as const;

export const ContentWrapper = ({
  component = "main",
  className,
  children,
  sx,
  ...rest
}: LayoutSectionProps) => (
  <Box
    component={component}
    data-testid="content-wrapper"
    className={classMerge("bg-gray-100", className)}
    sx={mergeSx(CONTENT_WRAPPER_SX, sx)}
    {...rest}>
    {children}
  </Box>
);

interface ContentProps extends LayoutSectionProps {
  fixedContent?: boolean;
  showBreadcrumbs?: boolean;
}

/** Page gutters. Shared with anything that has to bleed past them (`BeeEditor`, `LoadingScreen`). */
export const contentPadding = {
  left: "0.75rem",
  right: "1.25rem",
  top: "0.75rem",
  bottom: "1.25rem",
};

/**
 * A focused input paints its ring *outside* its border box (`shadows.input`, 2px), and every box
 * from here down to the page body clips — `overflow-x` cannot stay visible while `overflow-y`
 * scrolls, so there is no way to let the ring escape. Each clipping level therefore carries its own
 * padding and gives the indent straight back, until the overshoot lands in `contentPadding.left`.
 *
 * `ui/data-grid/fill-height-page` repeats this as `pl-2.5 -ml-2.5` — the same 10px, expressed in
 * Tailwind because that component is not on `sx`. Both are needed: it is the innermost clipper.
 */
const FOCUS_RING_GUTTER = "0.625rem";

export const Content = ({
  fixedContent = false,
  showBreadcrumbs = true,
  component = "article",
  className,
  children,
  sx,
}: ContentProps) => (
  <Box
    component={component}
    data-testid="content"
    className={classMerge("bg-gray-100", className)}
    sx={mergeSx(
      {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        overflowY: fixedContent ? "hidden" : "auto",
        overflowX: fixedContent ? "hidden" : undefined,
        minWidth: fixedContent ? 0 : undefined,
        flex: fixedContent ? "1" : undefined,
        minHeight: fixedContent ? 0 : undefined,
        mt: "0!important",
      },
      sx,
    )}>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: "1",
        minHeight: fixedContent ? 0 : undefined,
        minWidth: fixedContent ? 0 : undefined,
        paddingLeft: contentPadding.left,
        paddingRight: contentPadding.right,
        overflow: fixedContent ? "hidden" : undefined,
      }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          paddingTop: showBreadcrumbs ? 0 : contentPadding.top,
          paddingBottom: contentPadding.bottom,
          paddingLeft: FOCUS_RING_GUTTER,
          marginLeft: `-${FOCUS_RING_GUTTER}`,
          overflow: fixedContent ? "hidden" : undefined,
          flex: "1",
          minHeight: fixedContent ? 0 : undefined,
          minWidth: fixedContent ? 0 : undefined,
        }}>
        <Flash sx={{ mb: 2, mt: 2 }} />
        {children}
      </Box>
    </Box>
  </Box>
);
